/**
 * Portal Adapter
 *
 * Bridges canonical PascalCase keys (e.g. `Image`, `RepeatedForm`) to Portal's
 * legacy form-builder internal names (e.g. `image_camera`, `repeated_form`).
 *
 * Portal's external contract with the API is canonical PascalCase
 * (`FieldType.IMAGE === "Image"`). The internal form-builder UI uses lowercase
 * snake_case strings rooted in the original drag-and-drop registry.
 *
 * @deprecated Plan to remove the portalInternal layer by 2027-Q1; track via
 * follow-up task. Until then, this is the ONLY place that knows the legacy
 * mapping — `schemaConverter.js` and friends route through here.
 */

import { resolveType } from './resolvers';

/** Canonical PascalCase → Portal internal name. */
const CANONICAL_TO_PORTAL = {
  Text: 'input',
  TextArea: 'textarea',
  Email: 'email',
  Phone: 'phone',
  Url: 'url',
  Number: 'number',
  Currency: 'currency',
  Percentage: 'percentage',
  Select: 'select',
  Radio: 'radio',
  Checkbox: 'checkbox',
  CheckboxGroup: 'checkbox_group',
  Toggle: 'toggle',
  Tags: 'tags',
  QRCode: 'qr_scanner',
  Barcode: 'barcode_scanner',
  Date: 'date',
  Time: 'time',
  DateTime: 'datetime',
  Image: 'image_camera',
  Drawing: 'drawing',
  Voice: 'voice',
  File: 'file',
  Location: 'geolocation',
  Divider: 'divider',
  Header: 'header',
  Spacer: 'spacer',
  Signature: 'signature',
  Rating: 'rating',
  Slider: 'slider',
  RepeatedForm: 'repeated_form',
};

/** Portal internal name → canonical PascalCase. Built from the inverse. */
const PORTAL_TO_CANONICAL = Object.fromEntries(
  Object.entries(CANONICAL_TO_PORTAL).map(([k, v]) => [v, k])
);

/**
 * Resolve any input (canonical, internal, alias, numeric code) to Portal's
 * internal form-builder type string (e.g. `"image_camera"`).
 */
export function toPortalInternal(input) {
  const canonical = resolveType(input);
  return CANONICAL_TO_PORTAL[canonical] ?? canonical.toLowerCase();
}

/**
 * Resolve a Portal internal type string to the canonical PascalCase key
 * (e.g. `"image_camera"` → `"Image"`). Falls through to the registry resolver
 * for inputs that are already canonical or aliases.
 */
export function fromPortalInternal(input) {
  if (typeof input === 'string' && PORTAL_TO_CANONICAL[input]) {
    return PORTAL_TO_CANONICAL[input];
  }
  return resolveType(input);
}
